import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { space, width, flex, themeGet } from 'styled-system';

import ReactDatePicker from 'src/lib/react-datetime';
import 'src/lib/react-datetime/css/react-datetime.css';
import { getStyledSystemProps } from 'src/helpers';
import { basicTheme } from 'src/theme';
import { controlStyle, controlSize, errorsStyles } from './styles';
import { Label } from './Label';
import { BaseStyledInput } from './StyledInput';

export const StyledDatepicker = React.forwardRef(
  (
    {
      children,
      name,
      className,
      label,
      dateFormat,
      placeholder,
      required,
      disabled,
      error,
      value,
      onChange,
      onBlur,
      tabIndex,
      ...rest
    }: any,
    ref
  ) => {
    const [styledProps, restProps] = getStyledSystemProps(rest);
    const isValidISOString = value && moment(value, moment.ISO_8601).isValid();
    const currentValue = isValidISOString ? moment(value).format(dateFormat) : value;

    return (
      <StyledContainer ref={ref} className={className} {...styledProps}>
        <Label label={label} disabled={disabled} required={required}>
          <StyledReactDatePicker
            dateFormat={dateFormat}
            value={currentValue}
            onChange={v => (typeof v === 'string' ? onChange(v) : onChange(v?.format(dateFormat)))}
            closeOnSelect={true}
            onBlur={onBlur}
            renderInput={props => (
              <div css={{ position: 'relative' }}>
                <BaseStyledInput
                  {...props}
                  id={name}
                  className=""
                  {...{
                    placeholder,
                    disabled,
                    onBlur,
                    tabIndex,
                    error
                  }}
                />
              </div>
            )}
            {...restProps}
          />
        </Label>
        {children}
      </StyledContainer>
    );
  }
);

const StyledContainer = styled.div`
  position: relative;
  flex: 1;
  ${space};
  ${width};
  ${flex};

  & .rdtPicker {
    z-index: 1000;
    min-width: 160px;
    padding: 5px;
    margin: 2px 0 0;
    list-style: none;
    font-size: 14px;
    text-align: left;
    background-color: #fff;
    border: 1px solid #ccc;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
    background-clip: padding-box;
    font-family: ${themeGet('fonts.roboto')};
    font-weight: ${themeGet('fontWeights.light')};

    :after {
      content: '';
      display: inline-block;
      position: absolute;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid white;
      top: -6px;
      left: 8px;
    }

    :before {
      content: '';
      display: inline-block;
      position: absolute;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-bottom: 7px solid #ccc;
      border-bottom-color: rgba(0, 0, 0, 0.2);
      top: -7px;
      left: 7px;
    }

    .rdtMonth {
      width: 54px;
      height: 54px;
      line-height: 54px;
      margin: 2px 1.5px;
      cursor: pointer;
      border-radius: 4px;
    }

    table:first-child {
      margin-bottom: 10px;
    }

    th {
      border: none;
    }

    td.rdtActive {
      background-color: #60d2e8;
      color: ${themeGet('colors.white')};
      :hover {
        background-color: #60d2e8;
      }
    }
  }
`;

type StyledDatepickerProps = {
  error?: boolean;
  variant?: keyof typeof basicTheme.buttons;
  size?: keyof typeof basicTheme.controlSizes;
};
const StyledReactDatePicker = styled(ReactDatePicker)<StyledDatepickerProps>`
  & input {
    width: 100%;
    ${controlStyle};
    ${controlSize};
    ${errorsStyles};
  }
`;
StyledReactDatePicker.defaultProps = {
  size: 'sm'
};
